import { NotificationType } from 'hooks/useNotification';
import type {
  ChecklistItemKind,
  OrgEventChecklistItemDef,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { MEMPHIS_RESPONSE_CHECKLIST_ITEMS } from '../NotificationsTab/memphisThreatNotification';
import { USCG_FOC_SE_RESPONSE_CHECKLIST_ITEMS } from '../NotificationsTab/uscgFocSoutheastThreatNotification';

const MEMPHIS_BY_ID = Object.fromEntries(
  MEMPHIS_RESPONSE_CHECKLIST_ITEMS.map((item) => [item.id, item.label])
) as Record<string, string>;

const FOC_SE_BY_ID = Object.fromEntries(
  USCG_FOC_SE_RESPONSE_CHECKLIST_ITEMS.map((item) => [item.id, item.label])
) as Record<string, string>;

/**
 * Seeded checklist library for Event Settings (CFA Memphis + USCG FOC SE +
 * extra notify templates).
 */
export const RESPONSE_CHECKLIST_LIBRARY: OrgEventChecklistItemDef[] = [
  {
    id: 'memphis-checklist-notify-ops',
    kind: 'send_notification',
    label: MEMPHIS_BY_ID['memphis-checklist-notify-ops'],
    notify: {
      notificationTypes: [NotificationType.SMS, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Franchise alert: {eventName}',
      bodyTemplate:
        '{eventName}: notify franchise operators for stores inside the forecast cone ({resourceCount} resources).',
      emailSubject: 'Franchise alert: {eventName}',
      emailMessage:
        '{eventName}: notify franchise operators (SMS/email) for all Chick-fil-A stores inside the Helene forecast cone.',
      smsMessage:
        '{eventName}: franchise operators — stores in Helene cone. Check PRATUS for details.',
    },
  },
  {
    id: 'memphis-checklist-assign-prep',
    kind: 'org_action',
    label: MEMPHIS_BY_ID['memphis-checklist-assign-prep'],
    description:
      'CFA Helene demo — Planned / Unassigned org action from response checklist library.',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'memphis-checklist-monitor-category',
    kind: 'org_action',
    label: MEMPHIS_BY_ID['memphis-checklist-monitor-category'],
    description:
      'CFA Helene demo — Planned / Unassigned org action from response checklist library.',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'memphis-checklist-stand-up-workspace',
    kind: 'org_action',
    label: MEMPHIS_BY_ID['memphis-checklist-stand-up-workspace'],
    description:
      'CFA Helene demo — Planned / Unassigned org action from response checklist library.',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'library-notify-leadership-brief',
    kind: 'send_notification',
    label: 'Leadership brief — cone impact summary',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'watch',
      titleTemplate: 'Leadership brief: {eventName}',
      bodyTemplate:
        '{eventName}: {resourceCount} resources in cone. Threshold: {thresholdDescription}',
      emailSubject: 'Leadership brief: {eventName}',
      emailMessage:
        'Leadership brief for {eventName}.\n\n{stormName} impacts {resourceCount} resources.\n\n{thresholdDescription}',
      appMessage:
        '{eventName}: leadership brief — {resourceCount} resources in cone.',
    },
  },
  {
    id: 'library-notify-ops-status',
    kind: 'send_notification',
    label: 'Ops status update — stores in cone',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.TEAMS],
      priority: 'advisory',
      titleTemplate: 'Ops status: {eventName}',
      bodyTemplate:
        'Ops status update for {eventName}. Stores in cone: {resourceCount}. Feature: {featureName}.',
      teamsMessage:
        'Ops status: {eventName} — {resourceCount} stores/resources in cone ({featureName}).',
      appMessage:
        'Ops status: {eventName} — {resourceCount} resources in cone.',
    },
  },
  {
    id: 'foc-se-checklist-review-acp-grs',
    kind: 'org_action',
    label: FOC_SE_BY_ID['foc-se-checklist-review-acp-grs'],
    description:
      'USCG FOC Southeast demo — Planned / Unassigned org action from response checklist library.',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'foc-se-checklist-notify-sectors',
    kind: 'org_action',
    label: FOC_SE_BY_ID['foc-se-checklist-notify-sectors'],
    description:
      'USCG FOC Southeast demo — Planned / Unassigned org action from response checklist library.',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'foc-se-checklist-stage-sar',
    kind: 'org_action',
    label: FOC_SE_BY_ID['foc-se-checklist-stage-sar'],
    description:
      'USCG FOC Southeast demo — Planned / Unassigned org action from response checklist library.',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'foc-se-checklist-stand-up-workspace',
    kind: 'org_action',
    label: FOC_SE_BY_ID['foc-se-checklist-stand-up-workspace'],
    description:
      'USCG FOC Southeast demo — Planned / Unassigned org action from response checklist library.',
    orgAction: { status: 'Planned', poc: null },
  },
];

function newChecklistItemId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `custom-${crypto.randomUUID()}`;
  }
  return `custom-${Math.random().toString(36).slice(2)}`;
}

/**
 * Empty draft for the "Create new" entries on the add row. A notification
 * template with a blank label is stored as "Send Notification".
 */
export function blankChecklistItem(
  kind: ChecklistItemKind
): OrgEventChecklistItemDef {
  const id = newChecklistItemId();
  if (kind === 'send_notification') {
    return {
      id,
      kind: 'send_notification',
      label: '',
      notify: {
        notificationTypes: [NotificationType.IN_APP],
        priority: 'watch',
        titleTemplate: '',
        bodyTemplate: '',
      },
    };
  }
  return {
    id,
    kind: 'org_action',
    label: '',
    orgAction: { status: 'Planned', poc: null },
  };
}

export function cloneChecklistLibraryItem(
  item: OrgEventChecklistItemDef
): OrgEventChecklistItemDef {
  return {
    ...item,
    notify: item.notify
      ? {
          ...item.notify,
          userIds: item.notify.userIds ? [...item.notify.userIds] : undefined,
          teamIds: item.notify.teamIds ? [...item.notify.teamIds] : undefined,
        }
      : undefined,
    orgAction: item.orgAction
      ? {
          ...item.orgAction,
          checklist: item.orgAction.checklist?.map((entry) => ({ ...entry })),
          resourceRequirements: item.orgAction.resourceRequirements
            ? [...item.orgAction.resourceRequirements]
            : undefined,
          hazards: item.orgAction.hazards
            ? [...item.orgAction.hazards]
            : undefined,
          attachments: item.orgAction.attachments
            ? [...item.orgAction.attachments]
            : undefined,
          dependsOn: item.orgAction.dependsOn
            ? [...item.orgAction.dependsOn]
            : undefined,
        }
      : undefined,
  };
}
